import { useState } from "react";
import NumberInput from "../NumberInput";
import {
  Recipe,
  Ingredient,
  Cat,
  CAT_ORDER,
  UNITS,
  FoodKind,
  KIND_ORDER,
  KIND_META,
} from "./recipes";

interface Props {
  initial: Recipe;
  isBuiltin: boolean; // built-ins can be reset to default but not deleted
  onSave: (r: Recipe) => void;
  onCancel: () => void;
  onReset?: () => void; // revert a built-in override
  onDelete?: () => void; // remove a custom recipe
}

// Modal form to create or adjust a recipe (name, type, portions, ingredients).
export const RecipeEditor = ({ initial, isBuiltin, onSave, onCancel, onReset, onDelete }: Props) => {
  const [draft, setDraft] = useState<Recipe>({
    ...initial,
    ingredients: initial.ingredients.map((x) => ({ ...x })),
  });
  const set = (patch: Partial<Recipe>) => setDraft((d) => ({ ...d, ...patch }));

  const setIng = (idx: number, patch: Partial<Ingredient>) =>
    setDraft((d) => ({ ...d, ingredients: d.ingredients.map((x, k) => (k === idx ? { ...x, ...patch } : x)) }));
  const addIng = () =>
    setDraft((d) => ({ ...d, ingredients: [...d.ingredients, { name: "", qty: 1, unit: "st", cat: "Övrigt" }] }));
  const removeIng = (idx: number) =>
    setDraft((d) => ({ ...d, ingredients: d.ingredients.filter((_, k) => k !== idx) }));

  const save = () => {
    const cleaned: Recipe = {
      ...draft,
      name: draft.name.trim() || "Namnlös rätt",
      emoji: draft.emoji.trim() || "🍽️",
      servings: Math.max(1, Math.min(12, Math.round(draft.servings) || 1)),
      tags: draft.tags.map((t) => t.trim()).filter(Boolean),
      ingredients: draft.ingredients
        .map((x) => ({ ...x, name: x.name.trim(), qty: Math.max(0, x.qty) || 1 }))
        .filter((x) => x.name),
    };
    onSave(cleaned);
  };

  return (
    <div className="food__overlay" onClick={onCancel}>
      <div className="food__modal" onClick={(e) => e.stopPropagation()}>
        <div className="food__modal-head">
          <h3 className="food__modal-title">{isBuiltin ? "Justera rätt" : "Recept"}</h3>
          <button className="food__modal-close" onClick={onCancel} aria-label="Stäng">×</button>
        </div>

        <div className="food__modal-body">
          <div className="food__form-row">
            <input
              className="food__input food__input--emoji"
              value={draft.emoji}
              onChange={(e) => set({ emoji: e.target.value })}
              aria-label="Emoji"
            />
            <input
              className="food__input"
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Namn på rätten"
              autoFocus
            />
          </div>

          <div className="food__form-row">
            <div className="food__kind-chips">
              {KIND_ORDER.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`food__kind-chip${draft.kind === k ? " food__kind-chip--on" : ""}`}
                  onClick={() => set({ kind: k as FoodKind })}
                >
                  {KIND_META[k].icon} {KIND_META[k].label}
                </button>
              ))}
            </div>
          </div>

          <div className="food__form-row food__form-row--split">
            <label className="food__check">
              <input type="checkbox" checked={draft.matlada} onChange={(e) => set({ matlada: e.target.checked })} />
              🍱 Bra som matlåda
            </label>
            <label className="food__field">
              <span>Matlådor (receptets mängd)</span>
              <NumberInput
                className="food__input food__input--num"
                min={1}
                max={12}
                integer
                value={draft.servings}
                onChange={(n) => set({ servings: n })}
              />
            </label>
          </div>

          <label className="food__field">
            <span>Taggar (komma-separerat)</span>
            <input
              className="food__input"
              value={draft.tags.join(", ")}
              onChange={(e) => set({ tags: e.target.value.split(",") })}
              placeholder="t.ex. Snabb, Barnvänlig"
            />
          </label>

          <span className="food__form-label">Ingredienser</span>
          <div className="food__ing-list">
            {draft.ingredients.map((ing, idx) => (
              <div key={idx} className="food__ing-row">
                <input
                  className="food__input food__ing-name"
                  value={ing.name}
                  onChange={(e) => setIng(idx, { name: e.target.value })}
                  placeholder="Ingrediens"
                />
                <NumberInput
                  className="food__input food__ing-qty"
                  min={0}
                  step="0.5"
                  value={ing.qty}
                  onChange={(n) => setIng(idx, { qty: n })}
                />
                <select className="food__select" value={ing.unit} onChange={(e) => setIng(idx, { unit: e.target.value })}>
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <select
                  className="food__select"
                  value={ing.cat}
                  onChange={(e) => setIng(idx, { cat: e.target.value as Cat })}
                >
                  {CAT_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="food__icon-btn food__icon-btn--del" title="Ta bort" onClick={() => removeIng(idx)}>×</button>
              </div>
            ))}
            <button className="food__add" onClick={addIng}>+ Ingrediens</button>
          </div>
        </div>

        <div className="food__modal-foot">
          {isBuiltin && onReset && (
            <button className="food__link-btn" onClick={onReset}>↺ Återställ original</button>
          )}
          {!isBuiltin && onDelete && (
            <button className="food__link-btn food__link-btn--danger" onClick={onDelete}>Ta bort recept</button>
          )}
          <span className="food__foot-spacer" />
          <button className="food__btn food__btn--ghost" onClick={onCancel}>Avbryt</button>
          <button className="food__btn" onClick={save}>Spara</button>
        </div>
      </div>
    </div>
  );
};
